import type { RouteLocationNormalized, RouteLocationRaw, RouteParams } from "vue-router";

import { DeveloperNotificationException } from "@core/core/exceptions/DeveloperNotificationException";
import { BenutzerKompetenz } from "@core/core/types/benutzer/BenutzerKompetenz";
import { ServerMode } from "@core/core/types/ServerMode";
import type { TabData } from "@ui/ui/nav/TabData";

import type { SchuelerAbiturProps } from "~/components/schueler/abitur/SchuelerAbiturProps";
import { RouteDataSchuelerAbitur } from "~/router/apps/schueler/abitur/RouteDataSchuelerAbitur";
import { routeSchuelerAbiturZulassung } from "~/router/apps/schueler/abitur/RouteSchuelerAbiturZulassung";
import { type RouteSchueler, routeSchueler } from "~/router/apps/schueler/RouteSchueler";
import { routeError } from "~/router/error/RouteError";
import { schulformenGymOb } from "~/router/RouteHelper";
import { RouteManager } from "~/router/RouteManager";
import { RouteNode } from "~/router/RouteNode";
import { benutzerStateImpl } from "~/states/BenutzerStateImpl";
import { useSchuelerAuswahlState } from "~/states/schueler/SchuelerAuswahlState";

import { routeSchuelerAbiturPruefungsuebersicht } from "./RouteSchuelerAbiturPruefungsuebersicht";

const SchuelerAbitur = () => import("~/components/schueler/abitur/SchuelerAbitur.vue");


export class RouteSchuelerAbitur extends RouteNode<RouteDataSchuelerAbitur, RouteSchueler> {

	public constructor() {
		super(schulformenGymOb, [
			BenutzerKompetenz.ABITUR_ANSEHEN_ALLGEMEIN,
			BenutzerKompetenz.ABITUR_ANSEHEN_FUNKTIONSBEZOGEN,
		], "schueler.abitur", "abitur", SchuelerAbitur, new RouteDataSchuelerAbitur());
		super.mode = ServerMode.DEV;
		super.propHandler = (route) => this.getProps(route);
		super.text = "Abitur";
		this.isHidden = (params?: RouteParams) => {
			return this.checkHidden(params);
		};
		super.children = [
			routeSchuelerAbiturZulassung,
			routeSchuelerAbiturPruefungsuebersicht,
		];
		super.defaultChild = routeSchuelerAbiturZulassung;
	}

	protected checkHidden(params?: RouteParams) {
		try {
			const { id } = (params !== undefined) ? RouteNode.getIntParams(params, ["id"]) : { id: undefined };
			const schuelerAuswahlState = useSchuelerAuswahlState();
			if (!schuelerAuswahlState.manager.hasDaten()) {
				return false;
			}
			const auswahl = schuelerAuswahlState.manager.auswahl();
			if (((auswahl.abiturjahrgang !== null) && schuelerAuswahlState.manager.abiturjahrgaenge.get(auswahl.abiturjahrgang))
				&& (benutzerStateImpl.benutzerHatKompetenz(BenutzerKompetenz.ABITUR_ANSEHEN_ALLGEMEIN)
					|| (benutzerStateImpl.benutzerHatKompetenz(BenutzerKompetenz.ABITUR_ANSEHEN_FUNKTIONSBEZOGEN)
						&& benutzerStateImpl.kompetenzenAbiturjahrgaenge.has(auswahl.abiturjahrgang)))
				&& ((auswahl.jahrgang === 'Q1') || (auswahl.jahrgang === 'Q2'))) {
				return false;
			}
			return routeSchueler.getRouteDefaultChild({ id });
		} catch (e) {
			return routeError.getSimpleErrorRoute(e as DeveloperNotificationException);
		}
	}

	protected async update(to: RouteNode<any, any>, to_params: RouteParams, from: RouteNode<any, any> | undefined, from_params: RouteParams, isEntering: boolean): Promise<void | Error | RouteLocationRaw> {
		try {
			const { id } = RouteNode.getIntParams(to_params, ["id"]);
			if (id === undefined) {
				throw new DeveloperNotificationException("Fehler: Keine Schüler-ID in der URL angegeben.");
			}
			const schuelerAuswahlState = useSchuelerAuswahlState();
			const schueler = schuelerAuswahlState.manager.liste.get(id);
			if (schueler === null) {
				return routeSchueler.getRoute({ id });
			}
			try {
				await this.data.setSchueler(schueler, isEntering);
			} catch (error) {
				return routeSchueler.getRoute({ id });
			}
			if (to === this) {
				return this.getRouteView(this.data.view);
			}
			if (!to.name.startsWith(this.data.view.name)) {
				for (const child of this.children) {
					if (to.name.startsWith(child.name)) {
						this.data.setView(child, this.children);
					}
				}
			}
		} catch (e) {
			return routeError.getErrorRoute(e as DeveloperNotificationException);
		}
	}

	public async leave(from: RouteNode<any, any>, from_params: RouteParams): Promise<void> {
		this.data.reset();
	}

	public getProps(to: RouteLocationNormalized): SchuelerAbiturProps {
		return {
			tabManager: () => this.createTabManagerByChildren(this.data.view.name, this.setTab),
		};
	}

	private setTab = async (value: TabData) => {
		if (value.name === this.data.view.name) {
			return;
		}
		const node = RouteNode.getNodeByName(value.name);
		if (node === undefined) {
			throw new DeveloperNotificationException("Unbekannte Route");
		}
		await RouteManager.doRoute(this.getRouteView(node));
		this.data.setView(node, this.children);
	};

}

export const routeSchuelerAbitur = new RouteSchuelerAbitur();

